/**
 * Centralized error handling utility
 * Handles errors across the application with consistent logging and fallback behavior
 */

interface ErrorContext {
  functionName?: string;
  component?: string;
  operation?: string;
  [key: string]: any;
}

interface ErrorResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Handle errors with logging and optional console output
 * @param error - The error object or message
 * @param context - Additional context about where the error occurred
 * @param verbose - Whether to log to console (default: true in development)
 */
export const handleError = (
  error: unknown,
  context?: ErrorContext,
  verbose: boolean = process.env.NODE_ENV === "development",
): string => {
  const isDevelopment = process.env.NODE_ENV === "development";
  const contextStr = context
    ? ` [${Object.entries(context)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ")}]`
    : "";

  let errorMessage = "An unexpected error occurred";

  if (error instanceof Error) {
    errorMessage = error.message;
  } else if (typeof error === "string") {
    errorMessage = error;
  } else if (error instanceof Response) {
    errorMessage = `HTTP ${error.status}: ${error.statusText}`;
  }

  if (verbose) {
    console.error(
      `[ERROR]${contextStr}: ${errorMessage}`,
      isDevelopment ? error : "",
    );
  }

  return errorMessage;
};

/**
 * Wrap async operations with error handling
 * @param fn - The async function to execute
 * @param context - Additional context about the operation
 * @returns Promise with error handling
 */
export const withErrorHandler = async <T>(
  fn: () => Promise<T>,
  context?: ErrorContext,
): Promise<ErrorResponse<T>> => {
  try {
    const data = await fn();
    return { success: true, data };
  } catch (error) {
    const errorMessage = handleError(error, context);
    return { success: false, error: errorMessage };
  }
};

/**
 * Get user-friendly error message based on error type
 * @param error - The error object
 * @returns User-friendly error message
 */
export const getUserFriendlyErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    // Network errors
    if (error.message.includes("fetch")) {
      return "Unable to connect. Please check your internet connection.";
    }
    // Timeout errors
    if (error.message.includes("timeout")) {
      return "Request took too long. Please try again.";
    }
  }

  return "Something went wrong. Please try again later.";
};
